import React, {createContext, useContext, useMemo} from 'react';

import type {Ref as CustomRunColorsRef} from '../../state/views/customRunColors/types';
import type {Ref as CustomRunNamesRef} from '../../state/views/customRunNames/types';
import type {Ref as PanelBankConfigRef} from '../../state/views/panelBankConfig/types';
import type {WorkspaceViewRef} from '../../state/views/types';
import type {Ref as WorkspaceSettingsRef} from '../../state/views/workspaceSettings/types';
import {useDetectChanges} from '../../util/react-help/detectReactPropChanges';
import {usePanelBankActions} from '../PanelBank/headerControls/usePanelBankActions';

type WorkspaceRefs = {
  /** only undefined in RunWorkspace; should update to required once RunWorkspace is fully deprecated/removed */
  customRunColorsRef?: CustomRunColorsRef;
  /** only undefined in RunWorkspace; should update to required once RunWorkspace is fully deprecated/removed */
  customRunNamesRef?: CustomRunNamesRef;
  panelBankConfigRef: PanelBankConfigRef;
  viewRef: WorkspaceViewRef;
  workspaceSettingsRef: WorkspaceSettingsRef;
};

type WorkspaceRefsProviderProps = WorkspaceRefs & {
  children: React.ReactNode;
};

type WorkspaceRefsProviderValue = WorkspaceRefs & {
  panelBankActions: ReturnType<typeof usePanelBankActions>;
};

const WorkspaceRefsContext = createContext<
  WorkspaceRefsProviderValue | undefined
>(undefined);

export const WorkspaceRefsContextProvider = ({
  children,
  customRunColorsRef,
  customRunNamesRef,
  panelBankConfigRef,
  viewRef,
  workspaceSettingsRef,
}: WorkspaceRefsProviderProps) => {
  const panelBankActions = usePanelBankActions(panelBankConfigRef);
  const value = useMemo(
    () => ({
      customRunColorsRef,
      customRunNamesRef,
      panelBankActions,
      panelBankConfigRef,
      viewRef,
      workspaceSettingsRef,
    }),
    [
      customRunColorsRef,
      customRunNamesRef,
      panelBankActions,
      panelBankConfigRef,
      viewRef,
      workspaceSettingsRef,
    ]
  );

  useDetectChanges('WorkspaceRefsContextProvider', {
    customRunColorsRef,
    customRunNamesRef,
    panelBankActions,
    panelBankConfigRef,
    viewRef,
    workspaceSettingsRef,
  });

  return (
    <WorkspaceRefsContext.Provider value={value}>
      {children}
    </WorkspaceRefsContext.Provider>
  );
};

export const useWorkspaceRefsContext = () => {
  const value = useContext(WorkspaceRefsContext);

  if (value == null) {
    throw new Error(
      'useWorkspaceRefsContext must be used within a WorkspaceRefsContext'
    );
  }

  return value;
};

export const useMaybeWorkspaceRefsContext = ():
  | WorkspaceRefsProviderValue
  | undefined => {
  const value = useContext(WorkspaceRefsContext);
  return value;
};
